import React, { useState, useEffect, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextController from "../../form-fields/TextController";
import PercentageController from "../../form-fields/PercentageController";
import IntegerController from "../../form-fields/IntegerController"; // Import a number controller for the new columns
import { Button } from "../../ui/button";
import { TrashIcon } from "@radix-ui/react-icons";
import { PlusIcon, Clock } from "lucide-react";
import { debounce } from "../../../lib/utils";
import XanoAPI from "../../../lib/XanoAPI";
import { useToast } from "../../ui/use-toast";
import { Icons } from "../../../utils/icons";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import type { AutoSaveStatus } from "../../../hooks/useIntervalAutoSave";

interface Comparison {
  id: number;
  comparisonName: string;
  rateOfReturn: number;
  expectedIncomePercentage: number;
  yearsUntilIncome: number;
}

const formSchema = z.object({
  comparisons: z
    .array(
      z.object({
        id: z.number(),
        comparisonName: z.string().max(100),
        rateOfReturn: z.number().min(0).max(100),
        expectedIncomePercentage: z.number().min(0).max(100),
        yearsUntilIncome: z.number().min(0),
      })
    )
    .optional(),
});

type FormData = z.infer<typeof formSchema>;

const defaultItem: Comparison = {
  id: 0,
  comparisonName: "",
  rateOfReturn: 0,
  expectedIncomePercentage: 0,
  yearsUntilIncome: 0,
};

const emptyCollection = { values: [defaultItem] };

const ComparisonsComponent: React.FC<{
  client: ClientType;
  setComparisons: (comparisons: Comparison[]) => void;
  formsDirty: any;
  setFormsDirty: Function;
  fetchClientData: Function;
  resetFormsDirtyState: Function;
  saveStatus: AutoSaveStatus;
  lastSaveTime: number;
}> = ({
  client,
  setComparisons,
  formsDirty,
  setFormsDirty,
  fetchClientData,
  resetFormsDirtyState,
  saveStatus,
  lastSaveTime,
}) => {
  const api = XanoAPI.getInstance();
  const { toast } = useToast();
  const isAutoSaving = React.useRef(false);
  
  // Real-time auto-save text with live updates
  const [currentTime, setCurrentTime] = useState(Date.now());

  const parseJsonSafely = (json: string | undefined): Comparison[] => {
    if (!json) return emptyCollection.values;
    const values = JSON.parse(json).values;
    return values;
  };

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      comparisons: parseJsonSafely(client.comparisons),
    },
  });

  const { handleSubmit, setValue, getValues, formState, reset } = methods;
  const { isDirty } = formState;
  const [items, setItems] = useState(parseJsonSafely(client.comparisons));

  // Update current time every second for real-time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Sync dirty state with parent
  useEffect(() => {
    if (isDirty) {
      setFormsDirty((prev: any) => ({
        ...prev,
        comparisons: ["items"],
      }));
    } else {
      setFormsDirty((prev: any) => ({
        ...prev,
        comparisons: [],
      }));
    }
  }, [isDirty]);

  const debouncedAutoSave = useCallback(
    debounce(() => {
      const json = JSON.stringify({ values: getValues("comparisons") });
      setComparisons(getValues("comparisons") || []);
    }, 1000),
    [getValues, setComparisons]
  );

  useEffect(() => {
    debouncedAutoSave();
  }, [items, debouncedAutoSave]);

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const updatedItems = [...items, { ...defaultItem, id: items.length }];
    setItems(updatedItems);
    setValue("comparisons", updatedItems, { shouldDirty: true });
    debouncedAutoSave();
  };

  const deleteItem = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      e.preventDefault();
      setItems((current) => {
        const newItems = [...current];
        newItems.splice(index, 1);
        setValue("comparisons", newItems, { shouldDirty: true });
        debouncedAutoSave();
        return newItems;
      });
    },
    [setValue, debouncedAutoSave]
  );

  const handleInputChange = (
    index: number,
    field: keyof typeof defaultItem,
    value: string | number
  ) => {
    const updatedItems = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setItems(updatedItems);
    setValue("comparisons", updatedItems, { shouldDirty: true });
    debouncedAutoSave();
  };

  const onSubmit = async (data: FormData) => {
    setValue("comparisons", getValues("comparisons"));

    if (!client || !client.id) {
      console.error("Client ID is missing.");
      return;
    }

    const processedData = {
      ...client,
      comparisons: JSON.stringify({ values: data.comparisons }),
    };

    try {
      const updateClient = await api.updateClient(client.id, processedData);
      console.log("Client comparisons updated", updateClient);

      // Fetch updated data immediately
      await fetchClientData(client.id);

      // Reset the form to clear dirty state
      reset({ comparisons: data.comparisons });

      // Clear the parent's dirty state
      resetFormsDirtyState();

      // Only show toast notification on manual save, not auto-save
      if (!isAutoSaving.current) {
        toast({
          title: "Comparisons Updated!",
          description: "Your client's comparisons have been updated.",
        });
      }
      
      // Reset the auto-save flag
      isAutoSaving.current = false;
    } catch (error) {
      console.error(error);
      if (!isAutoSaving.current) {
        toast({
          title: "Error",
          description: "Failed to update comparisons.",
          variant: "destructive",
        });
      }
    }
  };

  // Format auto-save time
  const getAutoSaveText = () => {
    if (lastSaveTime === 0) return null;
    
    const timeSinceLastSave = currentTime - lastSaveTime;
    const seconds = Math.floor(timeSinceLastSave / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `Autosaved ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (seconds > 5) {
      return `Autosaved ${seconds} seconds ago`;
    } else if (seconds > 0) {
      return "Autosaved just now";
    }
    return null;
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, (errors) => console.log(errors))}>
        <Card className="mb-8">
          <CardHeader>
            <h4 className="text-lg font-semibold text-gray-700">Comparisons</h4>
          </CardHeader>
          <CardContent
            className="grid grid-cols-1 md:grid-cols-[2fr_2fr_2fr_2fr_auto] gap-x-8"
            id="comparisons-items"
          >
            {items &&
              items.map((item, index) => (
                <React.Fragment key={item.id}>
                  <div className="space-y-1 mb-1">
                    <TextController
                      name={`comparisons[${index}].comparisonName`}
                      label="Comparison Name"
                      placeholder="Comparison Name"
                      defaultValue={item.comparisonName}
                      onChange={(value: any) =>
                        handleInputChange(index, "comparisonName", value)
                      }
                    />
                  </div>

                  <div className="space-y-1 mb-2">
                    <PercentageController
                      name={`comparisons[${index}].rateOfReturn`}
                      label="Rate of Return"
                      placeholder="0.00"
                      defaultValue={item.rateOfReturn}
                      onChange={(value: any) =>
                        handleInputChange(index, "rateOfReturn", value)
                      }
                    />
                  </div>

                  <div className="space-y-1 mb-2">
                    <PercentageController
                      name={`comparisons[${index}].expectedIncomePercentage`}
                      label="Expected Income %"
                      placeholder="0.00"
                      defaultValue={item.expectedIncomePercentage}
                      onChange={(value: any) =>
                        handleInputChange(
                          index,
                          "expectedIncomePercentage",
                          value
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1 mb-2">
                    <IntegerController
                      name={`comparisons[${index}].yearsUntilIncome`}
                      label="Years Until Income"
                      placeholder="0"
                      defaultValue={item.yearsUntilIncome}
                      onChange={(value: any) =>
                        handleInputChange(index, "yearsUntilIncome", value)
                      }
                    />
                  </div>

                  <div className="flex items-end mb-8">
                    <Button
                      id={`trash-item-${index}`}
                      variant="outline"
                      size="icon"
                      onClick={(e) => deleteItem(e, index)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </React.Fragment>
              ))}

            <Button
              size="icon"
              className="mt-0"
              id="add-comparison"
              onClick={handleAdd}
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
        <div className="fixed bottom-0 right-0 m-3 z-50 flex items-center gap-3">
          {getAutoSaveText() && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 rounded-md border border-gray-200">
              <Clock className="h-3.5 w-3.5 text-gray-500" />
              <span className="text-xs text-gray-600">
                {getAutoSaveText()}
              </span>
            </div>
          )}
          <Button
            type="submit"
            className="p-3 bg-accent"
            id="save-comparisons"
            disabled={!isDirty}
            onClick={(e) => {
              // Check if this is a manual click (has isTrusted property)
              if (e.isTrusted) {
                isAutoSaving.current = false;
              } else {
                // Programmatic click (auto-save)
                isAutoSaving.current = true;
              }
            }}
          >
            <Icons.save className="mr-2" />
            {isDirty ? "Save Changes" : "No Changes"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default ComparisonsComponent;
